import { useEffect, useState } from "react";
import api from "@/lib/api";
import { slugify } from "@/lib/geo";

export interface LocationOption {
  value: string;
  label: string;
  count?: number;
}

export interface LocationOptions {
  states: LocationOption[];
  cities: LocationOption[];
  neighborhoods: LocationOption[];
}

export interface LocationValue {
  state?: string;
  city?: string;
  neighborhood?: string;
}

export interface LocationContext {
  category?: string;
  q?: string;
}

export function optionLabel(option: LocationOption | null | undefined): string {
  if (!option) return "";
  return typeof option.count === "number" ? `${option.label} (${option.count})` : option.label;
}

export async function fetchStates(): Promise<LocationOption[]> {
  const { data } = await api.get("/locations/states");
  return data as LocationOption[];
}

export async function fetchCities(state: string): Promise<LocationOption[]> {
  if (!state) return [];
  const { data } = await api.get(`/locations/cities/${state}`);
  return data as LocationOption[];
}

export async function fetchNeighborhoods(state: string, city: string): Promise<LocationOption[]> {
  if (!state || !city) return [];
  const { data } = await api.get("/locations/neighborhoods", {
    params: { state, city },
  });
  return data as LocationOption[];
}

export async function fetchLocationOptions(params: Record<string, unknown> = {}): Promise<LocationOption[]> {
  const { data } = await api.get("/locations/options", { params });
  return data as LocationOption[];
}

// Match the option whose value/label slug equals the incoming value's slug.
// This handles accent / casing mismatches (e.g. "Sao Paulo" from URL vs.
// "São Paulo" from the DB).
const _slugCache = new Map<string, string>();
export function findOptionMatch(options: LocationOption[], value: string): LocationOption | null {
  if (!value) return null;
  const target = slugify(value);
  for (const o of options) {
    const sv = _slugCache.get(o.value) ?? (() => { const s = slugify(o.value); _slugCache.set(o.value, s); return s; })();
    const sl = _slugCache.get(o.label) ?? (() => { const s = slugify(o.label); _slugCache.set(o.label, s); return s; })();
    if (sv === target || sl === target) return o;
  }
  return null;
}

export function withSelectedOption(options: LocationOption[], value: string): LocationOption[] {
  if (!value) return options;
  const match = findOptionMatch(options, value);
  if (match) return options;
  return [{ value, label: value }, ...options];
}

// Canonical states + cities loader. Neighborhoods are free-text and validated
// only against the canonical city list (no separate endpoint yet).
let _statesCache: Promise<LocationOption[]> | null = null;
export function fetchStatesCached(): Promise<LocationOption[]> {
  if (_statesCache) return _statesCache;
  _statesCache = api.get("/locations/states").then((r) => r.data as LocationOption[]);
  return _statesCache;
}
const _citiesCache: Record<string, Promise<LocationOption[]>> = {};
export function fetchCitiesCached(state: string): Promise<LocationOption[]> {
  if (!state) return Promise.resolve([]);
  if (!_citiesCache[state]) {
    _citiesCache[state] = api.get(`/locations/cities/${state}`).then((r) => r.data as LocationOption[]);
  }
  return _citiesCache[state];
}

export function useLocationOptions(value: LocationValue, context: LocationContext = {}): LocationOptions {
  const [options, setOptions] = useState<LocationOptions>({ states: [], cities: [], neighborhoods: [] });
  const { category, q } = context;

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        const states = await fetchStatesCached();
        if (!alive) return;
        const next: LocationOptions = {
          states: withSelectedOption(states, value.state || ""),
          cities: [],
          neighborhoods: [],
        };

        if (value.state) {
          try {
            const cities = await fetchCitiesCached(value.state);
            if (!alive) return;
            next.cities = withSelectedOption(
              cities,
              typeof value.city === "string" && /^\d/.test(value.city) ? value.city : (value.city || ""),
            );
            if (value.city) {
              try {
                const nb = await fetchNeighborhoods(value.state, value.city);
                if (!alive) return;
                next.neighborhoods = withSelectedOption(nb, value.neighborhood || "");
              } catch { /* ignore */ }
            }
          } catch { /* ignore */ }
        }
        if (alive) setOptions(next);
      } catch {
        if (alive) setOptions({ states: [], cities: [], neighborhoods: [] });
      }
    }
    load();
  }, [category, q, value.state, value.city, value.neighborhood]);

  return options;
}